import bytes from 'bytes';
import classNames from 'classnames';
import Download from 'components/general/Download/Download';
import Figure from 'components/general/Figure/Figure';
import HTMLBlock from 'components/general/HTMLBlock/HTMLBlock';
import Media from 'components/general/Media/Media';
import PullQuote from 'components/general/PullQuote/PullQuote';
import SiteImage from 'containers/SiteImage';
import * as R from 'ramda';
import React, { Fragment } from 'react';
import calculateAspectRatioFit from 'utils/calculateAspectRatioFit';
import prependAssetRoot from 'utils/prependAssetRoot';

/**
 * Tries to calculate soft crops based on width/height, but falls back to
 * generic hard-crops if w/h are omitted.
 */
const getCrops = (width, height) => {
  // Fallback crops
  if (!width || !height) {
    return [
      { w: 200, h: 100 },
      { w: 400, h: 200 },
      { w: 800, h: 400 },
      { w: 1600, h: 800 },
    ];
  }

  const clamped = calculateAspectRatioFit(width, height);

  // Soft crops
  return [
    clamped(320, 99999),
    clamped(640, 99999),
    clamped(960, 99999),
    clamped(1280, 99999),
    clamped(1920, 99999),
  ];
};

const componentMap = {
  related: {
    component: props => (
      <Media small {...props} />
    ),
    dataMap: R.applySpec({
      title: R.path(['fields', 'entity', 'data', 'title']),
      date: R.path(['fields', 'entity', 'data', 'created']),
      image: R.path(['fields', 'entity', 'data', 'image']),
      category: R.path(['fields', 'entity', 'data', 'category']),
      display: R.path(['fields', 'display', 'data', 'value']),
    }),
  },
  generalContent: {
    component: HTMLBlock,
    classes: ['html', 'restrict'],
    dataMap: R.applySpec({
      html: R.path(['richText', 'data']),
    }),
  },
  pullQuote: {
    classes: ['quote'],
    component: ({ content }) => {
      return (
        <PullQuote
          content={content}
        />
      );
    },
    dataMap: R.applySpec({
      content: R.path(['quote', 'data']),
      direction: R.path(['direction', 'data']),
      position: R.path(['position', 'data']),
    }),
  },
  image: {
    classes: ['image'],
    styles: ({ img }) => ({ maxWidth: img.width ? `${img.width}px` : null }),
    component: ({ caption, img }) => {
      const crops = getCrops(img.width, img.height);
      return caption ? (
        <Figure caption={caption}>
          <SiteImage {...img} crops={crops} />
        </Figure>
      ) : <SiteImage {...img} crops={crops} />;
    },
    dataMap: R.applySpec({
      img: {
        src: R.pipe(
          R.path(['image', 'data', 'path']),
          prependAssetRoot,
        ),
        alt: R.path(['imageAlternateText', 'data']),
        width: R.path(['image', 'data', 'width']),
        height: R.path(['image', 'data', 'height']),
      },
      caption: R.path(['imageCaption', 'data']),
      position: R.path(['position', 'data']),
    }),
  },
  download: {
    component: Download,
    classes: ['restrict', 'clear'],
    dataMap: R.applySpec({
      file: R.pipe(
        R.path(['download', 'data']),
        R.applySpec({
          path: R.pipe(
            R.prop('path'),
            prependAssetRoot,
          ),
          kind: R.prop('kind'),
          size: R.pipe(
            R.prop('size'),
            parseInt,
            bytes,
          ),
          filename: R.prop('filename'),
        }),
      ),
      label: R.path(['downloadLinkText', 'data']),
    }),
  },
};

const ContentMatrix: React.SFC<MatrixDef> = ({ blocks, classParent }) => (
  <Fragment>
    {blocks.map((block, index) => {
      const type = R.prop(block.key, componentMap);

      if (type) {
        const Component = type.component;
        const blockClasses = R.propOr([], 'classes', type);
        const blockProps = type.dataMap(R.path(['fields'], block));
        const position = R.prop('position', blockProps);

        const wrapperClassDef = {
          block: true,
          'block--aside': position === 'sidebar',
          'block--pull-left': position === 'left',
          'block--pull-right': position === 'right',
        };

        const wrapperClasses = R.pipe(
          // Add in modifiers defined in component map (will be prefixed with
          // classParent)
          R.merge(
            R.pipe(
              R.map((val: string) => ({
                [`block--${val}`]: true }
              )),
              R.mergeAll,
            )(blockClasses),
          ),
          // If we have a parent, re-add the above with the parent block as well
          R.when(
            R.always(!R.isNil(classParent)),
            R.pipe(
              R.toPairs,
              R.map(pair => [`${classParent}__${pair[0]}`, pair[1]]),
              R.fromPairs,
            ),
          ),
          // Add in base modifier classes
          R.merge(
            wrapperClassDef,
          ),
          // Add in modifiers defined in component map (un-prefixed)
          R.merge(
            R.pipe(
              R.map(mod => ({ [`block--${mod}`]: true })),
              R.mergeAll,
            )(blockClasses),
          ),
          classNames,
        )(wrapperClassDef);

        return (
          <div
            className={wrapperClasses}
            // Bad to put the index in, but don't always have uids...and these
            // will never be re-ordered/changed dynamically
            key={`${block.key}-${block.title}-${index}`}
            style={type.styles ? type.styles(blockProps) : null}
          >
            <Component {...blockProps} />
          </div>
        );
      }

      return null;
    })}
  </Fragment>
);

ContentMatrix.defaultProps = {
  blocks: [],
};

export interface BlockDef {
  key: string;
  [x: string]: any;
}

export interface MatrixDef {
  blocks: BlockDef[];
  classParent?: string;
  title?: string;
}

export default ContentMatrix;
